import React from 'react';

import TrashIcon from 'icons/ic-trash.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CheckboxField from 'components/application/CheckboxField';
import CircleQuestion from 'components/application/CircleQuestion';
import ColourInput from 'components/application/ColourInput';
import Tooltip from 'components/application/Tooltip';

import Uploader from 'components/uploader/Uploader';

const colourFields = [
  'primaryBgColour',
  'buttonBgColour',
  'buttonTextColour',
  'textLinkColour',
];

export default function Form({
  branding,
  disabled,
  invalidLogo,
  isPoweredByEditable,
  logoSrc,
  onColourChange,
  onColourToggle,
  onLabelToggle,
  onRemoveLogoClick,
  onSaveClick,
  onUpload,
  resetDefaults,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.projectConfiguration.branding.Form',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <>
      {colourFields.map((attribute) => {
        const handleBrandingColourClick = () => onColourToggle(attribute);

        const handleBrandingColourChange = ({ colour }) => {
          onColourChange({ colour, attributeName: attribute });
        };

        return (
          <div
            className='field-stack tw-max-w-[300px]'
            key={`colourField--${attribute}`}
          >
            <label className='field__label tw-font-medium'>
              {i18nT(`colourFields.${attribute}`)}
            </label>
            <ColourInput
              colour={branding[attribute].value}
              isOpen={branding[attribute].toggled}
              name={`branding[${attribute}]`}
              onChange={handleBrandingColourChange}
              onClick={handleBrandingColourClick}
            />
          </div>
        );
      })}
      <div className='field-stack tw-max-w-[300px]'>
        <label className='field__label tw-font-medium'>
          {i18nTShared('logo')}
        </label>
        <div>
          {logoSrc ?
            <>
              <div
                className='tw-mb-2 tw-border-1 tw-border-solid tw-px-4.5 tw-py-0'
                style={{ backgroundColor: branding.primaryBgColour.value }}
              >
                <img
                  alt={i18nT('logo.imageAlt')}
                  className='image--bound-scaling tw-max-h-[126px]'
                  src={logoSrc}
                />
              </div>
              <OutlinedButton color='red' onClick={onRemoveLogoClick} size='sm'>
                <TrashIcon className='tw-mr-1' height={20} width={20} />
                <span>{i18nTShared('deleteLogo')}</span>
              </OutlinedButton>
            </>
          : <Uploader
              externalInputProps={{ name: 'branding[logo]' }}
              fileTypes={['JPG', 'PNG']}
              isFieldInvalid={invalidLogo}
              mode='solo'
              onUpload={onUpload}
              showIcon={false}
              uploadsCount={0}
            />
          }
          {invalidLogo && (
            <div className='attachments__uploader-error-message tw-text-red-800'>
              {i18nT('logo.invalid')}
            </div>
          )}
        </div>
      </div>
      <div className='field-stack tw-max-w-[300px]'>
        <label className='field__label tw-font-medium'>
          {i18nTShared('poweredByHandsHQ')}
          <span className='hhq-checkbox__tooltip hhq-checkbox__tooltip--left'>
            <Tooltip
              placement='top'
              tooltip={i18nT('handshqLabel.tooltip')}
              trigger='hover'
            >
              <CircleQuestion />
            </Tooltip>
          </span>
        </label>
        <CheckboxField
          checked={branding.handshqLabel || !isPoweredByEditable}
          disabled={!isPoweredByEditable}
          label={i18nT('handshqLabel.label')}
          labelProps={{ className: 'hhq-checkbox__label--no-disabled-state' }}
          name='branding[handshqLabel]'
          onChange={onLabelToggle}
          value='true'
        />
        {!isPoweredByEditable && (
          <div className='tw-ml-8'>
            <div className='tw-mt-2 tw-text-s tw-font-normal tw-tracking-wide'>
              <Trans
                components={{
                  a: (
                    /* eslint-disable jsx-a11y/anchor-has-content */
                    <a
                      className='tw-font-medium tw-text-blue-500 hover:tw-text-blue-300'
                      href='https://intercom.help/hands-hq/en/articles/5058888-customise-the-look-branding-of-your-emails-remove-powered-by-handshq'
                      target='blank'
                    />
                    /* eslint-enable jsx-a11y/anchor-has-content */
                  ),
                }}
                i18nKey='settings.projectConfiguration.branding.Form.handshqLabel.disclaimer'
                ns='components'
              />
            </div>
          </div>
        )}
      </div>
      <FilledButton
        className='tw-mb-3 tw-w-max'
        color='blue'
        disabled={disabled || invalidLogo}
        onClick={onSaveClick}
      >
        {i18nTShared('saveChanges')}
      </FilledButton>
      <button
        className='app-link app-link--left-aligned tw-bg-transparent tw-text-s tw-font-medium tw-tracking-wide tw-text-blue-500 hover:tw-text-blue-300'
        onClick={resetDefaults}
      >
        {i18nT('reset')}
      </button>
    </>
  );
}

Form.propTypes = {
  branding: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  logoSrc: PropTypes.string,
  onSaveClick: PropTypes.func.isRequired,
  onColourToggle: PropTypes.func.isRequired,
  onColourChange: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onLabelToggle: PropTypes.func.isRequired,
  onRemoveLogoClick: PropTypes.func.isRequired,
};

Form.defaultProps = {
  branding: {
    primaryBgColour: { value: '#FFFFFF', toggled: false },
    buttonBgColour: { value: '#FFFFFF', toggled: false },
    buttonTextColour: { value: '#FFFFFF', toggled: false },
    textLinkColour: { value: '#FFFFFF', toggled: false },
    handshqLabel: true,
  },
  disabled: false,
  onSaveClick: () => {},
  onColourToggle: () => {},
  onColourChange: () => {},
  onUpload: () => {},
  onLabelToggle: () => {},
  onRemoveLogoClick: () => {},
};
